import React from 'react';
import { cn } from './cn';

export type ChipTone = 'neutral' | 'brand' | 'sev-critical' | 'sev-major' | 'sev-minor' | 'sev-maintenance';

export interface ChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Toggleable filter chip — renders pressed state. */
  pressed?: boolean;
  /** Render as <button>. Default true; pass false for a static chip. */
  asButton?: boolean;
  tone?: ChipTone;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  /** Small colored dot. */
  dot?: boolean;
  /** A numeric count to render after the label (pill). */
  count?: number;
}

const TONE_UNPRESSED: Record<ChipTone, string> = {
  neutral:          'bg-surface-1 text-ink-muted border border-border hover:bg-surface-2 hover:text-ink',
  brand:            'bg-surface-1 text-ink-muted border border-border hover:bg-brand-soft hover:text-brand',
  'sev-critical':   'bg-surface-1 text-ink-muted border border-border hover:bg-sev-critical-soft hover:text-sev-critical-ink',
  'sev-major':      'bg-surface-1 text-ink-muted border border-border hover:bg-sev-major-soft hover:text-sev-major-ink',
  'sev-minor':      'bg-surface-1 text-ink-muted border border-border hover:bg-sev-minor-soft hover:text-sev-minor-ink',
  'sev-maintenance':'bg-surface-1 text-ink-muted border border-border hover:bg-sev-maintenance-soft hover:text-sev-maintenance-ink',
};

// Active: ink-800 black fill. Hover-on-active lightens slightly to ink-700.
const PRESSED_DARK = 'bg-[var(--ink-800)] text-white border-[var(--ink-800)] hover:bg-[var(--ink-700)] hover:border-[var(--ink-700)]';

const TONE_PRESSED: Record<ChipTone, string> = {
  neutral:           PRESSED_DARK,
  brand:             PRESSED_DARK,
  'sev-critical':    PRESSED_DARK,
  'sev-major':       PRESSED_DARK,
  'sev-minor':       PRESSED_DARK,
  'sev-maintenance': PRESSED_DARK,
};

const DOT_TONE: Record<ChipTone, string> = {
  neutral: 'bg-ink-subtle', brand: 'bg-brand',
  'sev-critical': 'bg-sev-critical', 'sev-major': 'bg-sev-major',
  'sev-minor': 'bg-sev-minor', 'sev-maintenance': 'bg-sev-maintenance',
};

export const Chip: React.FC<ChipProps> = ({
  pressed = false, asButton = true, tone = 'neutral', leadingIcon, trailingIcon,
  dot = false, count, className, children, type = 'button', ...rest
}) => {
  const content = (
    <>
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 rounded-full flex-shrink-0', pressed ? 'bg-white' : DOT_TONE[tone])}
          aria-hidden
        />
      )}
      {leadingIcon && <span className="h-3.5 w-3.5 flex items-center shrink-0" aria-hidden>{leadingIcon}</span>}
      <span>{children}</span>
      {typeof count === 'number' && (
        <span
          className={cn(
            'ml-0.5 min-w-[1.25rem] text-center rounded-full text-[10.5px] font-semibold tabular-nums px-1.5 py-px',
            pressed ? 'bg-white/20 text-current' : 'bg-surface-2 text-ink-subtle',
          )}
        >
          {count}
        </span>
      )}
      {trailingIcon && <span className="h-3.5 w-3.5 flex items-center shrink-0" aria-hidden>{trailingIcon}</span>}
    </>
  );

  const classes = cn(
    'inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-body-sm font-medium',
    'transition-all duration-180 ease-premium select-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0',
    pressed ? TONE_PRESSED[tone] : TONE_UNPRESSED[tone],
    className,
  );

  if (!asButton) {
    return <span className={classes} {...(rest as React.HTMLAttributes<HTMLSpanElement>)}>{content}</span>;
  }

  return (
    <button
      type={type}
      aria-pressed={pressed}
      className={classes}
      {...rest}
    >
      {content}
    </button>
  );
};
